import { Card } from "@/components/ui/Card";
import { DEVELOPMENT_PROJECT_LABELS, type DevelopmentProjectSlug } from "@/config/constants";
import type { DevelopmentProject, DevelopmentUpdate } from "@/data/types";
import { cn } from "@/lib/cn";
import { formatIst } from "@/lib/time";

const STATUS_LABEL: Record<DevelopmentProject["status"], string> = {
  planned: "Planned",
  "in-progress": "In progress",
  completed: "Completed",
  "on-hold": "On hold",
};

const STATUS_TONE: Record<DevelopmentProject["status"], string> = {
  planned: "text-muted",
  "in-progress": "text-masjidGreen",
  completed: "text-goldText",
  "on-hold": "text-clay",
};

interface Props {
  project: DevelopmentProject;
  latestUpdate?: DevelopmentUpdate;
}

export function DevelopmentRow({ project, latestUpdate }: Props) {
  const fallbackTitle =
    DEVELOPMENT_PROJECT_LABELS[project.slug as DevelopmentProjectSlug] ?? project.slug;
  const updateDate = latestUpdate?.postedAt?.toDate
    ? latestUpdate.postedAt.toDate()
    : null;
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-serif text-lg text-ink">{project.title || fallbackTitle}</h3>
          {project.description ? (
            <p className="mt-1 text-sm leading-relaxed text-muted">{project.description}</p>
          ) : null}
        </div>
        <div className={cn("shrink-0 label-eyebrow", STATUS_TONE[project.status])}>
          {STATUS_LABEL[project.status]}
        </div>
      </div>
      {project.budgetPlaceholder ? (
        <div className="mt-3 rounded-md bg-cream/60 px-3 py-2 text-xs text-muted">
          {project.budgetPlaceholder}
        </div>
      ) : null}
      {latestUpdate ? (
        <div className="mt-4 border-t border-line pt-3">
          <div className="text-[10px] uppercase tracking-widest text-masjidGreen/80">
            Latest update
          </div>
          <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-ink/80">
            {latestUpdate.body}
          </p>
          {latestUpdate.photoUrl ? (
            <a
              href={latestUpdate.photoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs text-masjidGreen hover:underline"
            >
              View photo
            </a>
          ) : null}
          {updateDate ? (
            <div className="mt-2 text-xs text-muted">
              {formatIst(updateDate, "d MMM yyyy")}
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
